import { useMemo } from "react";
import en from "@/i18n/error-locales/en.json";
import ja from "@/i18n/error-locales/ja.json";
import ko from "@/i18n/error-locales/ko.json";
import ar from "@/i18n/error-locales/ar.json";
import ptBr from "@/i18n/error-locales/pt-br.json";
import es from "@/i18n/error-locales/es.json";
import de from "@/i18n/error-locales/de.json";
import fr from "@/i18n/error-locales/fr.json";
import zhHk from "@/i18n/error-locales/zh-hk.json";
import hi from "@/i18n/error-locales/hi.json";

export interface ErrorLocaleCatalog {
  locale: string;
  dir: "ltr" | "rtl";
  ui: Record<string, string>;
  /** `notice`: an extra sentence shown under the message for one category only, e.g.
   * invite_required (specs/access-requests S-12: the administrators were told). */
  categories: Record<string, { title: string; message: string; notice?: string }>;
}

/**
 * Error-page locale catalogs, generated from
 * packages/design-system/error-page/locales/*.json by scripts/sync-error-page.mjs.
 * Not the same as apps/website's i18n system — this is a lightweight,
 * dependency-free key -> locale catalog scoped to error-page copy, chosen
 * because no i18n library (react-intl, i18next, ...) is already a
 * dependency of this app or its sibling apps (ALL-4857 STEP 0 finding).
 */
export const ERROR_LOCALES: Record<string, ErrorLocaleCatalog> = {
  en: en as ErrorLocaleCatalog,
  ja: ja as ErrorLocaleCatalog,
  ko: ko as ErrorLocaleCatalog,
  ar: ar as ErrorLocaleCatalog,
  "pt-br": ptBr as ErrorLocaleCatalog,
  es: es as ErrorLocaleCatalog,
  de: de as ErrorLocaleCatalog,
  fr: fr as ErrorLocaleCatalog,
  "zh-hk": zhHk as ErrorLocaleCatalog,
  hi: hi as ErrorLocaleCatalog,
};

export const DEFAULT_ERROR_LOCALE = "en";

/** Own-property lookup, not `ERROR_LOCALES[code]`: the catalog is a plain
 * object literal, so an inherited key a caller controls (`constructor`,
 * `toString`, `__proto__`, ...) is truthy but is not a locale — indexing
 * would resolve `?locale=toString` to a function and the hook would render
 * `undefined` copy. Only a shipped key counts. */
function hasLocale(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(ERROR_LOCALES, code);
}

/** Resolves the active locale for the error page: an explicit `?locale=`
 * query param (used by Playwright to cover 2+ locales deterministically)
 * takes priority over the browser's language, which takes priority over
 * the default. */
export function resolveErrorLocale(searchParams: URLSearchParams): string {
  const requested = searchParams.get("locale");
  if (requested && hasLocale(requested)) return requested;

  if (typeof navigator !== "undefined") {
    const nav = navigator.language?.toLowerCase();
    if (nav && hasLocale(nav)) return nav;
    const short = nav?.split("-")[0];
    if (short && hasLocale(short)) return short;
  }
  return DEFAULT_ERROR_LOCALE;
}

export function useErrorLocale(searchParams: URLSearchParams): ErrorLocaleCatalog {
  return useMemo(() => {
    const code = resolveErrorLocale(searchParams);
    return ERROR_LOCALES[code] ?? ERROR_LOCALES[DEFAULT_ERROR_LOCALE];
  }, [searchParams]);
}
